import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { subscriptionService } from '../../../services/community/subscriptionService';
import { businessSubscriptionService } from '../../../services/business/subscriptionService';
import businessService from '../../../services/business/businessService';
import communityService from '../../../services/community/communityService';

interface PaymentCompletionPageProps {
  businessMode?: boolean;
}

export default function PaymentCompletionPage({ businessMode }: PaymentCompletionPageProps): JSX.Element {
  const [status, setStatus] = useState<string>('loading');
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus: string = params.get('redirect_status')!;
    const customerId = params.get('customerId');
    const type = params.get('type');

    // hold a reference to whichever timer we end up creating so the cleanup
    // logic can always run regardless of which code path executed.
    let poller: ReturnType<typeof setInterval> | null = null;

    if (!redirectStatus) {
      navigate(businessMode ? '/business/subscription' : '/overview');
    } else {
      if (customerId && status === 'loading' && type === 'setup') {
        if (businessMode) {
          let attempts = 0;
          const maxAttempts = 15;
          const interval = 2000;

          poller = setInterval(async () => {
            attempts += 1;
            try {
              const details = await businessSubscriptionService.getPaymentDetails();
              if (details) {
                if (poller) clearInterval(poller);
                await businessService.fetchBusinesses();
                navigate('/business/subscription');
              }
            } catch {
              // still waiting
            }

            if (attempts >= maxAttempts) {
              if (poller) clearInterval(poller);
              businessService.fetchBusinesses().finally(() => navigate('/business/subscription'));
            }
          }, interval);
        } else if (community && community.id) {
          setStatus('loading');
          let attempts = 0;
          const maxAttempts = 10;
          const interval = 3000;

          poller = setInterval(async () => {
            attempts += 1;
            try {
              const details = await subscriptionService.getPaymentDetails();
              if (details) {
                if (poller) clearInterval(poller);
                await communityService.fetchCommunity();
                navigate('/subscription/manage');
              }
            } catch {
              // still waiting
            }

            if (attempts >= maxAttempts) {
              if (poller) clearInterval(poller);
              communityService.fetchCommunity().finally(() => navigate('/subscription/manage'));
            }
          }, interval);
        }
      }

      setStatus(redirectStatus);
    }

    return () => {
      if (poller) clearInterval(poller);
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-900 mb-4" />
        <h1 className="text-3xl font-extrabold text-primary mb-2">{t(`page.payment.loading`)}</h1>
        <p className="text-lg text-gray-600 font-medium text-center max-w-md">
          {t(`page.payment.loadingSubtitle`)}
        </p>
      </div>
    );
  }

  if (status === 'succeeded') {
    return (
      <div className="mx-auto my-auto flex flex-col">
        <h1 className="mx-auto text-3xl font-bold">{t(`toast.success.subscriptionPayment`)}</h1>
        <p className="mx-auto text-xl font-bold">
          {t(`toast.success.subscriptionPaymentSubtitle`)}
        </p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="mx-auto my-auto flex flex-col">
        <h1 className="mx-auto text-3xl font-bold">{t('page.payment.failedTitle')}</h1>
        <p className="mx-auto text-xl font-bold">{t('page.payment.failedSubtitle')}</p>
      </div>
    );
  }

  return <></>;
}
